import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, UserCheck, AlertCircle } from 'lucide-react';
import logo from "../assets/images/ABN1.png"; 

// --- Custom CSS for Animations ---
const CustomStyles = () => (
  <style>{`
    @keyframes blob {
      0% { transform: translate(0px, 0px) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    .animate-blob {
      animation: blob 7s infinite;
    }
    .animation-delay-2000 {
      animation-delay: 2s;
    }
    .animation-delay-4000 {
      animation-delay: 4s;
    }
    .glass-card {
      background: rgba(255, 255, 255, 0.90); /* High opacity white for Logo Visibility */
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.3);
    }
  `}</style>
);

const Login = ({ setIsAuthenticated }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/login`,{
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Login failed.');
            }

            localStorage.setItem("authToken", data.token);
            localStorage.setItem("user", JSON.stringify(data.user));

            if (setIsAuthenticated) setIsAuthenticated(true);
            navigate("/member/home");

        } catch (err) {
            console.error("Login Error:", err);
            setError(err.message || "Something went wrong.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-slate-900 font-sans p-4">
            <CustomStyles />

            {/* --- 1. Dynamic Background (Brand Theme) --- */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#0f172a] via-[#1e3a8a] to-[#1A87CF]"></div>
            
            {/* --- 2. Animated Blobs (Movement) --- */}
            <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-[#FB9724] rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>

            {/* --- 3. Main Glass Card --- */}
            <div className="relative w-full max-w-md glass-card rounded-[2.5rem] shadow-2xl overflow-hidden p-8 sm:p-10 animate-fade-in-up">
                
                {/* --- Logo Area (White BG ensures Blue Logo pops) --- */}
                <div className="flex flex-col items-center justify-center mb-8">
                    <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center mb-4 border-4 border-blue-50 transform hover:scale-105 transition-transform duration-300">
                        {logo ? (
                            <img src={logo} alt="ABN Logo" className="w-16 h-16 object-contain" />
                        ) : (
                            <UserCheck className="w-10 h-10 text-[#1A87CF]" />
                        )}
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 tracking-tight">Welcome Back</h2>
                    <p className="text-gray-500 text-sm mt-1">Login to your member account</p>
                </div>

                {/* --- Error Message --- */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start gap-3 shadow-sm animate-pulse">
                        <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700 font-medium">{error}</p>
                    </div>
                )}

                {/* --- Login Form --- */}
                <form onSubmit={handleLogin} className="space-y-6">
                    
                    {/* Email */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-[#1A87CF] transition-colors" />
                            </div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your Registered Email"
                                required
                                className="w-full pl-11 pr-4 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1A87CF] focus:bg-white transition-all duration-300 shadow-sm"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Password</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-[#1A87CF] transition-colors" />
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="w-full pl-11 pr-12 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1A87CF] focus:bg-white transition-all duration-300 shadow-sm"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-[#1A87CF] transition-colors focus:outline-none cursor-pointer"
                            >
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                        </div>
                        {/* <div className="flex justify-end">
                            <button type="button" className="text-xs font-semibold text-[#1A87CF] hover:text-[#0e4e7a] transition-colors">
                                Forgot Password?
                            </button>
                        </div> */}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-4 rounded-2xl text-lg font-bold text-white shadow-xl shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-blue-500/50 flex justify-center items-center gap-2 ${
                            isLoading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-[#1A87CF] to-[#0e4e7a] hover:from-[#1569A3] hover:to-[#0A3A5E]'
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin h-5 w-5 text-white" />
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <span>Sign In</span>
                                <ArrowRight className="h-5 w-5" />
                            </>
                        )}
                    </button>
                </form>

                {/* --- Footer Text --- */}
                {/* <div className="mt-8 text-center">
                    <p className="text-gray-500 text-sm">
                        Don't have an account?{' '}
                        <button 
                            onClick={() => navigate('/join')} 
                            className="font-bold text-[#FB9724] hover:text-[#e0851f] transition-colors underline decoration-2 decoration-transparent hover:decoration-[#FB9724]"
                        >
                            Join ABN
                        </button>
                    </p>
                </div> */}
            </div>
            
            {/* Copyright */}
            <div className="absolute bottom-4 text-center w-full z-10 text-white/40 text-xs">
                © 2026 Ahir Business Network
            </div>
        </div>
    );
};

// Simple loader icon component if not imported from lucide-react directly in older versions
const Loader2 = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
);

export default Login;